/** `body.platformer` — the player-driven platformer body. Two run models:
 * `momentum` (accelerate/slide, arcade feel) and `instant` (velocity = input).
 * Swept landings pick the highest top crossed; rising through a one-way
 * surface passes, a solid one stops the head. Either way crossing a
 * bumpable/solid underside signals `head-bump` (the bump-from-below verb).
 * A momentum body with `airAccel` 0 jumps a committed arc. A push (the push
 * protocol: a current, a conveyor, an updraft) carries it on top of its own
 * run and fall, through the same collisions. Integration pauses while
 * another module holds the body (the `heldBy` protocol). */
import { bool, int, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read } from '../module.js';
import { approach, clamp, confineX, headSurface, heldBy, landingSurface, pushVelocity, surfaces } from '../physics.js';

export type PlatformerConfig = {
  run: 'momentum' | 'instant';
  maxSpeed: number; accel: number; airAccel: number; friction: number; iceFriction: number;
  gravity: number; jumpSpeed: number; maxFall: number; coyoteTicks: number; jumpCut: number;
  /** Half-width fraction used for landing/bump spans (feet narrower than the box). */
  footSpan: number;
  variableJump: boolean;
};

export const platformerBody = defineModule<PlatformerConfig>({
  id: 'body.platformer', version: 1, attach: 'entity', role: 'movement', surfaceContact: true,
  summary: 'Player platformer body: run (momentum or instant), jump with coyote time, gravity, swept one-way landings and head bumps.',
  config: obj({
    run: oneOf('momentum', 'instant'), maxSpeed: num(0.5, 20), accel: num(1, 400), airAccel: num(0, 400),
    friction: num(0, 400), iceFriction: num(0, 400), gravity: num(1, 120), jumpSpeed: num(1, 60), maxFall: num(1, 80),
    coyoteTicks: int(0, 10), jumpCut: num(0, 1), footSpan: num(0.1, 1), variableJump: bool(),
  }),
  requires: ['body.surface'], events: ['jump', 'land'],
  presentation: { jump: { cue: 'jump' }, land: { cue: 'land' } },
  proofs: ['momentum-accelerates-and-slides', 'instant-run-stops', 'jump-apex-height', 'one-way-pass-up-land-down', 'solid-ceiling-stops-head', 'head-bump-signal', 'walk-off-edge-falls', 'ice-friction', 'committed-arc', 'push-protocol', 'held-body-pauses'],
  animStates: () => ['idle', 'move', 'jump'],
  init(_ctx, e) {
    e.grounded = true;
    Object.assign(_ctx.data(e), { airborne: 0, cut: false });
  },
  systems: {
    move(ctx, e) {
      if (!e || e.status !== 'active' || heldBy(e)) return;
      const c = ctx.config, d = ctx.data(e), dt = ctx.dt, world = ctx.world;
      const input = ctx.input(e.slot ?? 0);
      const list = surfaces(ctx);
      const half = (e.w / 2) * c.footSpan;
      const ground = e.groundId ? ctx.entity(e.groundId) : undefined;
      // Horizontal drive.
      if (c.run === 'instant') e.vx = input.dx * c.maxSpeed;
      else if (input.dx !== 0) e.vx = clamp(e.vx + input.dx * (e.grounded ? c.accel : c.airAccel) * dt, -c.maxSpeed, c.maxSpeed);
      else if (e.grounded) e.vx = approach(e.vx, 0, (ground?.tags.includes('frozen') ? c.iceFriction : c.friction) * dt);
      if (input.dx !== 0) e.facing = input.dx;
      // Jump (edge-triggered, coyote window) and variable-height cut.
      const airborne = read(d, 'airborne');
      if (input.a && !input.prevA && (e.grounded || airborne <= c.coyoteTicks)) {
        e.vy = -c.jumpSpeed; e.grounded = false; e.groundId = null; d.airborne = c.coyoteTicks + 1; d.cut = false;
        ctx.emit('jump', { x: e.x, y: e.y, slot: e.slot ?? 0 });
      } else if (c.variableJump && !input.a && e.vy < 0 && d.cut !== true) {
        e.vy *= c.jumpCut; d.cut = true;
      }
      e.vy = Math.min(e.vy + c.gravity * dt, c.maxFall);
      // The step: its own velocity plus every push about it this tick.
      const push = pushVelocity(ctx.signals('push'), e);
      const sx = e.vx + push.vx, sy = e.vy + push.vy;
      // Horizontal step + solid side pushout.
      e.x += sx * dt;
      for (const s of list) {
        if (s.mode !== 'solid' || e.y <= s.top + 0.001 || e.y - e.h >= s.bottom - 0.001) continue;
        const dx = e.x - s.entity.x, reach = s.entity.w / 2 + e.w / 2;
        if (Math.abs(dx) >= reach) continue;
        e.x = s.entity.x + Math.sign(dx || -sx || 1) * reach;
        e.vx = 0;
      }
      confineX(world, e);
      // Vertical step.
      const prevFeet = e.y, nextFeet = e.y + sy * dt;
      const wasGrounded = e.grounded;
      e.grounded = false; e.groundId = null;
      if (sy < 0 && nextFeet - e.h < 0) {
        // The world's top edge is a ceiling.
        e.y = e.h; e.vy = 0;
      } else if (sy < 0) {
        const hit = headSurface(world, list.filter(s => s.mode === 'solid' || s.bumpable), e.x, half, prevFeet - e.h, nextFeet - e.h);
        e.y = nextFeet;
        if (hit) {
          ctx.signal('head-bump', hit.entity, e, { x: e.x });
          if (hit.mode === 'solid') { e.y = hit.bottom + e.h; e.vy = 0; }
        }
      } else {
        const land = landingSurface(world, list, e.x, half, prevFeet, nextFeet);
        if (land) { e.y = land.top; e.vy = 0; e.grounded = true; e.groundId = land.entity.id; }
        else e.y = nextFeet;
      }
      d.airborne = e.grounded ? 0 : read(d, 'airborne') + 1;
      if (e.grounded && !wasGrounded) ctx.emit('land', { x: e.x, y: e.y, slot: e.slot ?? 0 });
      ctx.setAnim(e, !e.grounded ? 'jump' : Math.abs(e.vx) > 0.15 ? 'move' : 'idle');
    },
  },
});
